import { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, Checkbox, Input, Search, Tags } from "../../ui";
import { integrationLabel } from "../../../utils/integrations";
import { campaignTypeLabel } from "../operationUtils";
import { getCampaignText } from "./campaignActionsText";
import StatusBadge from "./StatusBadge";
import "./CampaignActions.css";

const TYPE_VARIANT = { deliveryCamp: "success", paidCamp: "danger", promoCamp: "warning" };
const PAGE_SIZE = 6;

const formatDate = (value, lang) =>
  value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", { timeZone: "UTC" }) : "";

// Searchable, filterable, paginated card grid used wherever a campaign has to
// be chosen. Items: { id, title, subtitle, type, status, appName, storeName,
// startDate, endDate, deletedAt, origin }. Pass `onSelect` to make the cards
// selectable; without it the grid is read-only.
export default function CampaignPicker({
  items,
  selectedId = null,
  onSelect,
  // Multi-select: pass the selected ids and a setter (checkbox cards plus
  // "select all" over the campaigns matching the current search/filter).
  selectedIds,
  onSelectedIdsChange,
  disabled = false,
  loading = false,
  error = "",
  onRetry,
  emptyText,
  pageSize = PAGE_SIZE,
}) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);

  const types = useMemo(() => [...new Set(items.map((item) => item.type).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (typeFilter && item.type !== typeFilter) return false;
      if (!query) return true;
      return [item.title, item.subtitle, item.storeName, integrationLabel(item.appName, lang)]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [items, search, typeFilter, lang]);

  const multiple = Array.isArray(selectedIds);
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((item) => selectedSet.has(item.id));
  const toggleOne = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange([...next]);
  };
  const toggleAll = () => {
    const next = new Set(selectedSet);
    filtered.forEach((item) => (allFilteredSelected ? next.delete(item.id) : next.add(item.id)));
    onSelectedIdsChange([...next]);
  };

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // A refreshed or narrowed list can leave the current page out of range.
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (loading) {
    return (
      <p className="ca-picker-state" role="status">
        {text.loading}
      </p>
    );
  }
  if (error) {
    return (
      <div className="ca-picker-state" role="alert">
        <p>{error}</p>
        {onRetry && (
          <Button size="sm" appearance="soft" onClick={onRetry}>
            {text.retry}
          </Button>
        )}
      </div>
    );
  }
  if (!items.length) {
    return (
      <p className="ca-picker-state" role="status">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="ca-picker">
      <div className="ca-picker-tools">
        <Input
          aria-label={text.search}
          placeholder={text.search}
          value={search}
          iconStart={<Search size={16} />}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
        {types.length > 1 && (
          <div className="ca-type-filter" role="group" aria-label={text.campaignType}>
            {["", ...types].map((type) => (
              <Button
                key={type || "all"}
                size="sm"
                variant="neutral"
                appearance={typeFilter === type ? "filled" : "outline"}
                aria-pressed={typeFilter === type}
                onClick={() => {
                  setTypeFilter(type);
                  setPage(1);
                }}
              >
                {type ? campaignTypeLabel(type, lang) : text.allTypes}
              </Button>
            ))}
          </div>
        )}
      </div>

      {multiple ? (
        <div className="ca-select-bar">
          <Checkbox
            checked={allFilteredSelected}
            onChange={toggleAll}
            disabled={disabled || filtered.length === 0}
            label={text.selectAll(filtered.length)}
          />
          <span className="ca-selected-count" role="status">
            {text.selectedCount(selectedSet.size)}
          </span>
        </div>
      ) : (
        <p className="ca-picker-count" role="status">
          {text.results(filtered.length)}
        </p>
      )}

      {visible.length === 0 ? (
        <p className="ca-picker-state">{text.noMatches}</p>
      ) : (
        <ul className="ca-picker-grid">
          {visible.map((item) => {
            const selected = selectedId === item.id;
            const dates =
              item.startDate || item.endDate
                ? `${formatDate(item.startDate, lang) || "—"} → ${formatDate(item.endDate, lang) || "—"}`
                : "";
            const body = (
              <>
                <span className="ca-card-top">
                  <Tags variant={TYPE_VARIANT[item.type] || "neutral"} appearance="soft" size="sm">
                    {campaignTypeLabel(item.type, lang)}
                  </Tags>
                  {item.status && <StatusBadge status={item.status} />}
                </span>
                <strong className="ca-card-title">{item.title}</strong>
                {item.subtitle && <span className="ca-card-sub">{item.subtitle}</span>}
                <span className="ca-card-meta">
                  {item.storeName && <span>{item.storeName}</span>}
                  {item.appName && <span>{integrationLabel(item.appName, lang)}</span>}
                  {dates && <span dir="ltr">{dates}</span>}
                  {item.origin === "alreadyActive" && <span>{text.alreadyActive}</span>}
                  {item.deletedAt && (
                    <span>
                      {text.deletedOn} {formatDate(item.deletedAt, lang)}
                    </span>
                  )}
                </span>
              </>
            );
            const checked = selectedSet.has(item.id);
            return (
              <li key={item.id}>
                {multiple ? (
                  <div
                    className={`ca-card is-selectable ${checked ? "is-selected" : ""}`}
                    onClick={() => !disabled && toggleOne(item.id)}
                  >
                    <span className="ca-card-check" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleOne(item.id)}
                        aria-label={item.title}
                      />
                    </span>
                    {body}
                  </div>
                ) : onSelect ? (
                  <button
                    type="button"
                    className={`ca-card is-selectable ${selected ? "is-selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => onSelect(selected ? null : item)}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="ca-card">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pages > 1 && (
        <nav className="ca-pager" aria-label={text.page(page, pages)}>
          <Button size="sm" appearance="soft" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {text.prev}
          </Button>
          <span>{text.page(page, pages)}</span>
          <Button size="sm" appearance="soft" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            {text.next}
          </Button>
        </nav>
      )}
    </div>
  );
}
